import type { NewLeagueTeam } from "../../../../../common/types/newLeague.ts";
import { formatSeasonRecord } from "../../util/seasonRecord.ts";

export type NewLeagueDraftMode = "random" | "real";

const realStartingPointMergerSeasons = new Set([1947, 1948, 1949, 1976]);

export const recoverRealStartingPhase = ({
  maxPhase,
  maxSeason,
  phase,
  playoffsPhase,
  season,
}: {
  maxPhase: number;
  maxSeason: number;
  phase: number;
  playoffsPhase: number;
  season: number;
}) => {
  if (season === maxSeason && phase > maxPhase) {
    return maxPhase;
  }
  if (realStartingPointMergerSeasons.has(season) && phase > playoffsPhase) {
    return playoffsPhase;
  }
  return phase;
};

export const getExplicitNewLeagueModeFromPathname = (
  pathname: string,
): NewLeagueDraftMode | undefined => {
  if (pathname === "/new_league/real") {
    return "real";
  }
  if (pathname === "/new_league/random") {
    return "random";
  }
};

export const filterNewLeagueDraftForPathname = <Draft extends object>(
  draft: Draft | undefined,
  pathname: string,
): Draft | undefined => {
  const draftMode = (draft as { mode?: unknown } | undefined)?.mode;
  if (!draft || (draftMode !== "real" && draftMode !== "random")) {
    return;
  }

  if (pathname === "/new_league") {
    return draft;
  }

  const explicitMode = getExplicitNewLeagueModeFromPathname(pathname);
  return explicitMode === draftMode ? draft : undefined;
};

export const newLeagueBasicStepIDs = ["mode", "team", "review"] as const;

export const newLeagueAdvancedSetupKeys = ["leagueName", "season", "phase", "difficulty"] as const;

export type NewLeagueSafeBaselines = {
  random: {
    difficulty: number;
    leagueName: string;
    mode: "random";
    startingSeason: string;
  };
  real: {
    difficulty: number;
    leagueName: string;
    mode: "real";
    phase: number;
    season: number;
  };
};

export const getNewLeagueSafeBaselines = ({
  currentYear,
  defaultDifficulty,
  defaultLeagueName,
  realPhase,
  realSeason,
}: {
  currentYear: number;
  defaultDifficulty: number;
  defaultLeagueName: string;
  realPhase: number;
  realSeason: number;
}): NewLeagueSafeBaselines => ({
  random: {
    difficulty: defaultDifficulty,
    leagueName: defaultLeagueName,
    mode: "random",
    startingSeason: String(currentYear),
  },
  real: {
    difficulty: defaultDifficulty,
    leagueName: defaultLeagueName,
    mode: "real",
    phase: realPhase,
    season: realSeason,
  },
});

export type NewLeagueTeamRecommendation = {
  reason: "selected" | "largeMarket" | "recentSeason" | "balanced";
  team: NewLeagueTeam;
};

export type NewLeagueTeamStrategy = "contender" | "middle" | "rebuild";

export type NewLeagueTeamDifficulty = "easy" | "medium" | "hard";

export type NewLeagueTeamResource =
  | {
      key: "market";
      rank: number;
      tied: boolean;
      totalTeams: number;
      value: string;
    }
  | {
      key: "population";
      millions: number;
      value: string;
    }
  | {
      key: "record";
      available: boolean;
      value: string;
    };

export type NewLeagueTeamProfile = {
  difficulty: NewLeagueTeamDifficulty;
  resources: [NewLeagueTeamResource, NewLeagueTeamResource, NewLeagueTeamResource];
  strategy: NewLeagueTeamStrategy;
};

const getRecommendationReason = (
  team: NewLeagueTeam,
  selectedTid: number | undefined,
): NewLeagueTeamRecommendation["reason"] => {
  if (team.tid === selectedTid) {
    return "selected";
  }

  if (team.season !== undefined) {
    return "recentSeason";
  }

  if (team.popRank <= 6) {
    return "largeMarket";
  }

  return "balanced";
};

export const getNewLeagueTeamRecommendations = ({
  limit = 3,
  selectedTid,
  teams,
}: {
  limit?: number;
  selectedTid?: number;
  teams: NewLeagueTeam[];
}): NewLeagueTeamRecommendation[] => {
  const availableTeams = teams.filter((team) => !team.disabled);
  const selectedTeam = availableTeams.find((team) => team.tid === selectedTid);
  const candidates = [
    ...(selectedTeam ? [selectedTeam] : []),
    ...availableTeams
      .filter((team) => team.tid !== selectedTeam?.tid)
      .slice()
      .sort((a, b) => {
        const popRankDiff = a.popRank - b.popRank;
        if (popRankDiff !== 0) {
          return popRankDiff;
        }

        return `${a.region} ${a.name}`.localeCompare(`${b.region} ${b.name}`);
      }),
  ].slice(0, limit);

  return candidates.map((team) => ({
    reason: getRecommendationReason(team, selectedTid),
    team,
  }));
};

export const getNewLeagueTeamProfile = (
  team: NewLeagueTeam,
  teamsOrTotal: NewLeagueTeam[] | number,
): NewLeagueTeamProfile => {
  const activeTeams = Array.isArray(teamsOrTotal)
    ? teamsOrTotal.filter((candidate) => !candidate.disabled)
    : undefined;
  const totalTeams =
    activeTeams?.length ?? (typeof teamsOrTotal === "number" ? teamsOrTotal : 0);
  const safeTotalTeams = Math.max(totalTeams, 1);
  const marketRank = activeTeams
    ? activeTeams.filter((candidate) => candidate.pop > team.pop).length + 1
    : Math.max(1, Math.floor(team.popRank));
  const marketRankTied = activeTeams
    ? activeTeams.filter((candidate) => candidate.pop === team.pop).length > 1
    : !Number.isInteger(team.popRank);
  const strongRecord =
    team.seasonInfo !== undefined && team.seasonInfo.won >= team.seasonInfo.lost + 10;
  const weakRecord =
    team.seasonInfo !== undefined && team.seasonInfo.lost >= team.seasonInfo.won + 10;
  const deepPlayoffRun = !!team.seasonInfo?.roundsWonText;

  let strategy: NewLeagueTeamStrategy;
  if (strongRecord || deepPlayoffRun || team.popRank <= Math.ceil(safeTotalTeams * 0.25)) {
    strategy = "contender";
  } else if (weakRecord || team.popRank >= Math.ceil(safeTotalTeams * 0.7)) {
    strategy = "rebuild";
  } else {
    strategy = "middle";
  }

  const difficulty: NewLeagueTeamDifficulty =
    strategy === "contender" ? "easy" : strategy === "rebuild" ? "hard" : "medium";

  const record = team.seasonInfo
    ? formatSeasonRecord(team.seasonInfo)
    : "";

  return {
    difficulty,
    resources: [
      {
        key: "market",
        rank: marketRank,
        tied: marketRankTied,
        totalTeams: safeTotalTeams,
        value: String(marketRank),
      },
      {
        key: "population",
        millions: team.pop,
        value: `${team.pop.toFixed(1)}M`,
      },
      {
        key: "record",
        available: team.seasonInfo !== undefined,
        value: record,
      },
    ],
    strategy,
  };
};
